import React, { useState, useContext, useEffect } from 'react';
import {
  MDBCard,
  MDBCardBody,
  MDBCardTitle,
  MDBBtn,
  MDBRow,
  MDBCol,
  MDBChipsInput,
  MDBSpinner,
  MDBAlert,
  MDBChip,
  MDBBadge
} from 'mdb-react-ui-kit';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../../App';
import { useCompanyTopics } from '../hooks/useCompanyTopics';
import { useEventData } from '../../../components-user/event-management/lecture-registration/hooks/useEventData';
import { getLocalizedText } from '../../../utils/localizedText';
import { useAdminFormData } from '../hooks/useAdminFormData';
import { DevContext } from '../../../App';
import { captureError } from '../../../utils/sentryHelpers';


export default function CompanyTopicsStep({ onComplete, stepId, isCompleted }) {
  const { t } = useTranslation();
  const { user } = useContext(UserContext);
  const { isDevMode } = useContext(DevContext);
  const { saveAllCompanyTopics, getCompanyTopics, isLoading, error } = useCompanyTopics();
  const { topics, topicGroups, fetchTopics, isLoading: isLoadingTopics } = useEventData();
  const { formData, handleFieldChange, getFieldValue } = useAdminFormData();
  
  // Get admin companies from user context
  const adminCompanies = user?.employeeProfiles?.items?.filter(
    profile => profile.role === 'admin' && profile.status === 'active'
  ) || [];
  
  // Get companies data from form context
  const companiesData = getFieldValue('companiesTopicsData', {});
  const [isValid, setIsValid] = useState(false);
  const [companiesWithExistingData, setCompaniesWithExistingData] = useState({});
  const [isLoadingData, setIsLoadingData] = useState(true);

  const handleTopicToggle = (companyId, topicId) => {
    const currentData = companiesData[companyId] || { topics: [], keywords: [] };
    const isSelected = currentData.topics.includes(topicId);
    
    // Find which topic group this topic belongs to
    const topicGroupId = topics.find(t => t.id === topicId)?.topicGroupTopicsId;
    
    // Get all topics in the same group that are currently selected
    const topicsInSameGroup = currentData.topics.filter(id => {
      const topic = topics.find(t => t.id === id);
      return topic?.topicGroupTopicsId === topicGroupId;
    });
    
    let newSelectedTopics;
    
    if (isSelected) {
      // Remove topic if already selected
      newSelectedTopics = currentData.topics.filter(id => id !== topicId);
    } else {
      // Add topic if not selected and under limit (max 3 per group)
      if (topicsInSameGroup.length < 3) {
        newSelectedTopics = [...currentData.topics, topicId];
      } else {
        // If at limit for this group, don't add
        return;
      }
    }
    
    const newCompaniesData = {
      ...companiesData,
      [companyId]: {
        ...currentData,
        topics: newSelectedTopics
      }
    };
    
    handleFieldChange('companiesTopicsData', newCompaniesData);
    validateAllCompanies(newCompaniesData);
  };

  const handleKeywordAdd = (companyId, tag) => {
    const currentData = companiesData[companyId] || { topics: [], keywords: [] };
    const currentKeywords = Array.isArray(currentData.keywords) ? currentData.keywords : [];
    
    // Add new keyword if not already present and under limit
    if (!currentKeywords.some(kw => (kw.tag || kw) === tag) && currentKeywords.length < 10) {
      const newKeywords = [...currentKeywords, { tag }];
      
      const newCompaniesData = {
        ...companiesData,
        [companyId]: {
          ...currentData,
          keywords: newKeywords
        }
      };
      
      handleFieldChange('companiesTopicsData', newCompaniesData);
      validateAllCompanies(newCompaniesData);
    }
  };

  const handleKeywordDelete = (companyId, tag) => {
    const currentData = companiesData[companyId] || { topics: [], keywords: [] };
    const currentKeywords = Array.isArray(currentData.keywords) ? currentData.keywords : [];
    
    // Remove keyword
    const newKeywords = currentKeywords.filter(kw => (kw.tag || kw) !== tag);
    
    const newCompaniesData = {
      ...companiesData,
      [companyId]: {
        ...currentData,
        keywords: newKeywords
      }
    };
    
    handleFieldChange('companiesTopicsData', newCompaniesData);
    validateAllCompanies(newCompaniesData);
  };
  
  const validateAllCompanies = (data) => {
    // Check if all companies have valid data
    const allValid = adminCompanies.every(profile => {
      const companyData = data[profile.company.id] || { topics: [], keywords: [] };
      
      // Check topics: at least 1 topic selected, max 3 per group
      const hasTopics = companyData.topics.length >= 1;
      
      // Check that no group has more than 3 topics
      const topicsByGroup = {};
      companyData.topics.forEach(topicId => {
        const topic = topics.find(t => t.id === topicId);
        if (topic) {
          const groupId = topic.topicGroupTopicsId;
          topicsByGroup[groupId] = (topicsByGroup[groupId] || 0) + 1;
        }
      });
      
      const validTopicCounts = Object.values(topicsByGroup).every(count => count <= 3);
      
      // Ensure keywords is an array and count properly
      const keywords = Array.isArray(companyData.keywords) ? companyData.keywords : [];
      const hasKeywords = keywords.length >= 3 && keywords.length <= 10;
      
      return hasTopics && validTopicCounts && hasKeywords;
    });
    
    setIsValid(allValid);
  };
  
  // Initialize companies data and load existing data
  useEffect(() => {
    const loadExistingData = async () => {
      setIsLoadingData(true);
      const initialData = {};
      const existingDataFlags = {};
      
      for (const profile of adminCompanies) {
        const companyId = profile.company.id;
        
        // Always load from backend on first mount
        const result = await getCompanyTopics(companyId);
        if (result.success) {
          // Convert string keywords to objects with tag property for MDBChipsInput
          const keywordObjects = result.data.keywords.map(kw => 
            typeof kw === 'string' ? { tag: kw } : kw
          );
          
          initialData[companyId] = {
            topics: result.data.topics || [],
            keywords: keywordObjects
          };
          existingDataFlags[companyId] = result.data.hasExistingData;
        } else {
          initialData[companyId] = { topics: [], keywords: [] };
          existingDataFlags[companyId] = false;
        }
      }
      
      handleFieldChange('companiesTopicsData', initialData);
      setCompaniesWithExistingData(existingDataFlags);
      setIsLoadingData(false);
    };
    
    if (adminCompanies.length > 0 && topics.length > 0) {
      loadExistingData();
    }
  }, [adminCompanies.length, topics.length, getCompanyTopics]);
  
  // Validate when companies data changes
  useEffect(() => {
    validateAllCompanies(companiesData);
  }, [companiesData]);
  
  // Fetch topics on mount
  useEffect(() => {
    if (topics.length === 0 && !isLoadingTopics) {
      fetchTopics();
    }
  }, [topics.length, isLoadingTopics, fetchTopics]);

  const handleComplete = async () => {
    if (isValid) {
      try {
        // Transform data for API call
        const dataToSave = adminCompanies.map(profile => {
          const companyData = companiesData[profile.company.id] || { topics: [], keywords: [] };
          const keywords = Array.isArray(companyData.keywords) ? companyData.keywords : [];
          
          return {
            companyId: profile.company.id,
            topics: companyData.topics || [],
            keywords: keywords.map(kw => 
              typeof kw === 'string' ? kw : kw.tag || kw
            )
          };
        });
        
        const result = await saveAllCompanyTopics(dataToSave);
        
        if (result.success) {
          onComplete(stepId);
        }
      } catch (err) {
        console.error('Error saving company topics:', err);
        captureError(err, {
          feature: 'admin-onboarding',
          operation: 'save-company-topics',
          consoleMessage: 'Error saving company topics:',
          context: {
            adminCompaniesCount: adminCompanies.length,
            stepId
          }
        });
      }
    }
  };
  
  // Get validation details for dev mode
  const getValidationDetails = () => {
    const details = adminCompanies.map(profile => {
      const companyData = companiesData[profile.company.id] || { topics: [], keywords: [] };
      
      // Check topics per group
      const topicsByGroup = {};
      companyData.topics.forEach(topicId => {
        const topic = topics.find(t => t.id === topicId);
        if (topic) {
          const groupId = topic.topicGroupTopicsId;
          topicsByGroup[groupId] = (topicsByGroup[groupId] || 0) + 1;
        }
      });
      
      const hasTopics = companyData.topics.length >= 1;
      const validTopicCounts = Object.values(topicsByGroup).every(count => count <= 3);
      const topicsValid = hasTopics && validTopicCounts;
      
      // Ensure keywords is properly handled
      const keywords = Array.isArray(companyData.keywords) ? companyData.keywords : [];
      const hasKeywords = keywords.length >= 3 && keywords.length <= 10;
      
      return {
        company: profile.company.name,
        companyId: profile.company.id,
        topicsCount: companyData.topics.length,
        topicsByGroup: topicsByGroup,
        keywordsCount: keywords.length,
        topicsValid: topicsValid,
        keywordsValid: hasKeywords,
        overallValid: topicsValid && hasKeywords,
        keywordsRaw: companyData.keywords, // For debugging
        hasExistingData: companiesWithExistingData[profile.company.id] || false
      };
    });
    
    return details;
  };
  
  // Render company section
  const renderCompanySection = (profile) => {
    const company = profile.company;
    const companyData = companiesData[company.id] || { topics: [], keywords: [] };
    const hasExistingData = companiesWithExistingData[company.id];
    
    return (
      <div key={company.id} className="mb-5">
        {/* Show info if data was previously set */}
        {hasExistingData && (
          <div className="p-3 mb-3 bg-warning bg-opacity-10 border border-info rounded">
            <div className="d-flex align-items-center">
              <i className="fas fa-info-circle text-danger me-2"></i>
              <span>{t('Topic areas and keywords have already been set by another administrator. You can modify these selections if needed.')}</span>
            </div>
          </div>
        )}
        
        {/* Company Header */}
        <MDBCard className="mb-3">
          <MDBCardBody>
            <div className="d-flex align-items-center">
              {company.image?.main?.fileName && (
                <img
                  src={`https://iec-cp-public.s3.eu-central-1.amazonaws.com/companyImages/${company.image.main.fileName}`}
                  alt={company.image.main.alt || company.name}
                  style={{ width: '60px', height: '60px', objectFit: 'contain' }}
                  className="me-3"
                />
              )}
              <div>
                <h5 className="mb-0">{company.name}</h5>
                <small className="text-muted">{t('Admin authorization')}</small>
              </div>
            </div>
          </MDBCardBody>
        </MDBCard>
        
        <MDBRow>
          {/* Topics Selection */}
          <MDBCol md="6">
            <MDBCard className="h-100">
              <MDBCardBody>
                <MDBCardTitle>{t('Topic Areas (select 1 to 3 per main area)')}</MDBCardTitle>
                <p className="small text-muted mb-3">
                  {t('Please select the topic areas that best match your company. This is an initial rough categorization.')}
                </p>
                {topicGroups.length === 0 ? (
                  <MDBAlert color="info">
                    {isLoadingTopics ? t('Loading topic areas...') : t('No topic areas available')}
                  </MDBAlert>
                ) : (
                  <MDBRow>
                    {topicGroups.map((topicGroup) => (
                      <MDBCol size="12" key={topicGroup.id} className="mb-3">
                        <div className="mb-2">
                          <h6 className="fw-bold text-primary">
                            {getLocalizedText(topicGroup.name)}
                          </h6>
                        </div>
                        <div className="d-flex flex-wrap gap-1">
                          {topicGroup.topics.items.map((topic) => {
                            const isSelected = companyData.topics.includes(topic.id);
                            return (
                              <MDBChip
                                key={topic.id}
                                className="cursor-pointer"
                                size="sm"
                                onClick={() => handleTopicToggle(company.id, topic.id)}
                                style={{
                                  cursor: 'pointer',
                                  backgroundColor: isSelected ? "#3dd378" : "#ececec",
                                  color: isSelected ? "white" : "black"
                                }}
                              >
                                {getLocalizedText(topic.name)}
                              </MDBChip>
                            );
                          })}
                        </div>
                      </MDBCol>
                    ))}
                  </MDBRow>
                )}
                <div className="text-muted small mt-3">
                  {(() => {
                    // Calculate topics per group
                    const topicsByGroup = {};
                    companyData.topics.forEach(topicId => {
                      const topic = topics.find(t => t.id === topicId);
                      if (topic) {
                        const groupId = topic.topicGroupTopicsId;
                        const groupName = topicGroups.find(g => g.id === groupId)?.name?.[0]?.text || 'Unknown';
                        topicsByGroup[groupName] = (topicsByGroup[groupName] || 0) + 1;
                      }
                    });
                    
                    return (
                      <>
                        {t('Selected')}: {companyData.topics.length} {t('total')} ({t('min.')} 1)
                        {Object.entries(topicsByGroup).length > 0 && (
                          <span className="ms-2">
                            [{Object.entries(topicsByGroup).map(([group, count], idx) => 
                              `${group}: ${count}/3`
                            ).join(', ')}]
                          </span>
                        )}
                      </>
                    );
                  })()}
                </div>
              </MDBCardBody>
            </MDBCard>
          </MDBCol>
          
          {/* Keywords Input */}
          <MDBCol md="6">
            <MDBCard className="h-100">
              <MDBCardBody>
                <MDBCardTitle>{t('Keywords (3 to 10 freely selectable)')}</MDBCardTitle>
                <p className="small text-muted mb-3">
                  {t('Tag your company\'s competencies. Here you can go deeper and better describe your company. Click in the input field and enter only one word at a time (do not write texts). Confirm with the Enter key and then enter the next keyword.')}
                </p>
                <MDBChipsInput
                  key={`keywords-${company.id}-${companyData.keywords?.length || 0}`}
                  label={t('Enter keywords and press Enter')}
                  editable
                  initialValues={Array.isArray(companyData.keywords) ? companyData.keywords : []}
                  onAdd={(tag) => handleKeywordAdd(company.id, tag)}
                  onDelete={(tag) => handleKeywordDelete(company.id, tag)}
                />
                <div className="text-muted small mt-3">
                  {t('Entered keywords')}: {Array.isArray(companyData.keywords) ? companyData.keywords.length : 0} {t('of')} 10 ({t('min.')} 3)
                </div>
              </MDBCardBody>
            </MDBCard>
          </MDBCol>
        </MDBRow>
      </div>
    );
  };
  
  // Render dev info
  const renderDevInfo = () => {
    if (!isDevMode) return null;
    
    const validationDetails = getValidationDetails();
    
    return (
      <MDBRow className="mb-4">
        <MDBCol>
          <MDBCard className="border-info">
            <MDBCardBody>
              <h6 className="mb-3">🔍 Validation Debug Info (Dev Mode) - Company Topics Step</h6>
              
              <div className="mb-2">
                <strong>Admin Companies:</strong> {adminCompanies.length}
              </div>
              
              <div className="mb-2">
                <strong>Overall Valid:</strong>{' '}
                <MDBBadge color={isValid ? 'success' : 'danger'}>
                  {isValid ? 'Valid' : 'Invalid'}
                </MDBBadge>
              </div>
              
              <div className="mb-2">
                <strong>Company Details:</strong>
                <ul className="mb-0 mt-1">
                  {validationDetails.map(detail => (
                    <li key={detail.companyId} className="mb-1">
                      <strong>{detail.company}:</strong> 
                      {detail.hasExistingData && (
                        <MDBBadge size="sm" color="info" className="ms-1">
                          Has Existing Data
                        </MDBBadge>
                      )}
                      <br />
                      Topics: {detail.topicsCount} total
                      {Object.keys(detail.topicsByGroup).length > 0 && (
                        <span className="ms-1">
                          (Groups: {Object.entries(detail.topicsByGroup).map(([groupId, count]) => 
                            `${count}/3`
                          ).join(', ')})
                        </span>
                      )}
                      <MDBBadge 
                        size="sm" 
                        color={detail.topicsValid ? 'success' : 'warning'}
                        className="ms-1"
                      >
                        {detail.topicsValid ? 'OK' : 'Invalid'}
                      </MDBBadge>
                      {' | '}
                      Keywords: {detail.keywordsCount}/10 
                      <MDBBadge 
                        size="sm" 
                        color={detail.keywordsValid ? 'success' : 'warning'}
                        className="ms-1"
                      >
                        {detail.keywordsValid ? 'OK' : 'Invalid'}
                      </MDBBadge>
                      {' | Raw: '}
                      <code style={{ fontSize: '9px' }}>
                        {typeof detail.keywordsRaw === 'object' && detail.keywordsRaw !== null 
                          ? `[${detail.keywordsRaw.constructor?.name || 'Object'}]` 
                          : JSON.stringify(detail.keywordsRaw)
                        }
                      </code>
                      {' = '}
                      <MDBBadge 
                        size="sm" 
                        color={detail.overallValid ? 'success' : 'danger'}
                        className="ms-1"
                      >
                        {detail.overallValid ? 'VALID' : 'INVALID'}
                      </MDBBadge>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="mb-2">
                <strong>Topics Loaded:</strong>{' '}
                <MDBBadge color={topics.length > 0 ? 'success' : 'warning'}>
                  {topics.length} topics from {topicGroups.length} groups
                </MDBBadge>
              </div>
              
              <div className="mb-2">
                <strong>Form Data:</strong>
                <pre style={{ fontSize: '10px', maxHeight: '100px', overflow: 'auto' }}>
                  {JSON.stringify(companiesData, (key, value) => {
                    // Skip React-specific properties and circular references
                    if (key.startsWith('_') || 
                        key.startsWith('__react') || 
                        key === 'nativeEvent' ||
                        value instanceof HTMLElement) {
                      return '[Circular/HTMLElement]';
                    }
                    // Convert complex objects to simple representations
                    if (value && typeof value === 'object' && value.constructor !== Object && value.constructor !== Array) {
                      return `[${value.constructor.name}]`;
                    }
                    return value;
                  }, 2)}
                </pre>
              </div>
            </MDBCardBody>
          </MDBCard>
        </MDBCol>
      </MDBRow>
    );
  };

  return (
    <div className="h-100 d-flex flex-column">
      <div className="mb-4">
        <h3>{t('Assign Topic Areas and Keywords')}</h3>
        <p className="text-muted">
          {t('Now assign your company(ies) to specific topic areas and describe competencies with keywords. If there are multiple admins in your company, the admin who goes through the onboarding process first will answer these points. Other admins will see this selection/input displayed here. After completing the onboarding process, entries can be edited.')}
        </p>
      </div>

      {error && (
        <MDBAlert color="danger" className="mb-4">
          {error}
        </MDBAlert>
      )}

      {renderDevInfo()}
      
      <div className="flex-grow-1">
        {isLoadingData ? (
          <div className="text-center py-5">
            <MDBSpinner size="lg" />
            <p className="mt-3">{t('Loading company data...')}</p>
          </div>
        ) : adminCompanies.length === 0 ? (
          <MDBAlert color="warning">
            {t('You are not registered as administrator in any company.')}
          </MDBAlert>
        ) : (
          adminCompanies.map(profile => renderCompanySection(profile))
        )}
      </div>

      <div className="mt-4 d-flex justify-content-between align-items-end">
        {/* Progress Info */}
        <div className="flex-grow-1 me-4">
          <div className="mb-2">
            <h6 className="mb-1">{t('Progress')}:</h6>
            {adminCompanies.length === 0 ? (
              <MDBAlert color="warning" className="mb-0">
                {t('No admin companies found.')}
              </MDBAlert>
            ) : (
              <>
                {adminCompanies.map(profile => {
                  const companyData = companiesData[profile.company.id] || { topics: [], keywords: [] };
                  const keywords = Array.isArray(companyData.keywords) ? companyData.keywords : [];
                  
                  // Check topics per group
                  const topicsByGroup = {};
                  companyData.topics.forEach(topicId => {
                    const topic = topics.find(t => t.id === topicId);
                    if (topic) {
                      const groupId = topic.topicGroupTopicsId;
                      topicsByGroup[groupId] = (topicsByGroup[groupId] || 0) + 1;
                    }
                  });
                  
                  const hasTopics = companyData.topics.length >= 1;
                  const validTopicCounts = Object.values(topicsByGroup).every(count => count <= 3);
                  const topicsValid = hasTopics && validTopicCounts;
                  const hasKeywords = keywords.length >= 3 && keywords.length <= 10;
                  const isComplete = topicsValid && hasKeywords;
                  
                  return (
                    <div key={profile.company.id} className="mb-2 p-2 border rounded bg-light">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong>{profile.company.name}</strong>
                          <MDBBadge 
                            color={isComplete ? 'success' : 'warning'} 
                            className="ms-2"
                            size="sm"
                          >
                            {isComplete ? t('Complete') : t('Incomplete')}
                          </MDBBadge>
                        </div>
                        <div className="text-end small">
                          <div className={`${topicsValid ? 'text-success' : 'text-warning'}`}>
                            {t('Topics')}: {companyData.topics.length} {t('selected')} 
                            {topicsValid ? ' ✓' : ' ⚠️'}
                          </div>
                          <div className={`${hasKeywords ? 'text-success' : 'text-warning'}`}>
                            {t('Keywords')}: {keywords.length}/10 ({t('min.')} 3)
                            {hasKeywords ? ' ✓' : ' ⚠️'}
                          </div>
                        </div>
                      </div>
                      {!isComplete && (
                        <div className="mt-1 small text-muted">
                          {!hasTopics && <div>• {t('Select at least 1 topic area')}</div>}
                          {!validTopicCounts && hasTopics && <div>• {t('Maximum 3 topics per group')}</div>}
                          {!hasKeywords && <div>• {t('Enter at least')} {3 - keywords.length} {t('more keywords')}</div>}
                        </div>
                      )}
                    </div>
                  );
                })}
                
                <div className="mt-3 p-2 bg-primary bg-opacity-10 border border-primary border-opacity-25 rounded">
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="fw-bold">{t('Total')}:</span>
                    <div>
                      <MDBBadge 
                        color={isValid ? 'success' : 'danger'}
                        className="me-2"
                      >
                        {adminCompanies.filter(profile => {
                          const companyData = companiesData[profile.company.id] || { topics: [], keywords: [] };
                          const keywords = Array.isArray(companyData.keywords) ? companyData.keywords : [];
                          
                          // Check topics per group
                          const topicsByGroup = {};
                          companyData.topics.forEach(topicId => {
                            const topic = topics.find(t => t.id === topicId);
                            if (topic) {
                              const groupId = topic.topicGroupTopicsId;
                              topicsByGroup[groupId] = (topicsByGroup[groupId] || 0) + 1;
                            }
                          });
                          
                          const hasTopics = companyData.topics.length >= 1;
                          const validTopicCounts = Object.values(topicsByGroup).every(count => count <= 3);
                          const topicsValid = hasTopics && validTopicCounts;
                          const hasKeywords = keywords.length >= 3 && keywords.length <= 10;
                          return topicsValid && hasKeywords;
                        }).length} / {adminCompanies.length} {t('complete')}
                      </MDBBadge>
                      {isValid && <span className="text-success small">{t('Ready to save!')} ✓</span>}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        
        {/* Save Button */}
        <div>
          <MDBBtn 
            color="primary" 
            onClick={handleComplete}
            disabled={!isValid || isLoading || adminCompanies.length === 0}
            size="lg"
          >
            {isLoading ? <MDBSpinner size="sm" className="me-2" /> : null}
            {t('Save all companies')}
          </MDBBtn>
        </div>
      </div>
    </div>
  );
}